// KO splash rings on the void surface (§12.5, §11.2): each KO drops two expanding rings at the
// splash point, styled per void (a foam ring on liquids, a puff ring on clouds, an electric zap
// ring on Pinball's grid). One InstancedMesh of flat quads with a ring SDF, 12 slots.
import {
  AdditiveBlending, Color, InstancedBufferAttribute, InstancedMesh, Matrix4, NormalBlending, PlaneGeometry, ShaderMaterial, UniformsLib,
  UniformsUtils, type ColorRepresentation,
} from 'three';
import type { VoidKind } from '@shared/maps/mapTypes';
import { GLSL_NOISE } from '../kit/glsl';
import type { EnvUniforms } from '../kit/materials';
import { VOID_Y } from './void';

const SLOTS = 12;
const LIFE_S = 1.1;
const MAX_RADIUS = 3.4;

const VERTEX = /* glsl */ `
attribute vec2 aLife;   // x = birth time, y = strength
varying vec2 vUv;
varying float vAge;
varying float vStrength;
uniform float uTime;
#include <common>
#include <fog_pars_vertex>
void main() {
  vUv = uv;
  vAge = (uTime - aLife.x) / ${LIFE_S.toFixed(2)};
  vStrength = aLife.y;
  float k = clamp(vAge, 0.0, 1.0);
  float s = mix(0.2, 1.0, 1.0 - pow(1.0 - k, 3.0)) * aLife.y;
  vec3 p = position * s;
  vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  // Idle slots (never used: strength −100 scaled the quad to ~700 m, so it covered the screen and
  // every pixel ran the fragment shader just to discard; or finished) are collapsed outside the clip
  // volume and never rasterize (INT-B: twelve full-screen passes on software GL).
  if (vAge < 0.0 || vAge > 1.0 || aLife.y <= 0.0) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  #include <fog_vertex>
}`;

const FRAGMENT = /* glsl */ `
uniform vec3 uColor;
uniform vec3 uColor2;
uniform float uZap;
uniform float uTime;
varying vec2 vUv;
varying float vAge;
varying float vStrength;
#include <common>
#include <fog_pars_fragment>
${GLSL_NOISE}
void main() {
  if (vAge < 0.0 || vAge > 1.0) discard;
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float a = atan(p.y, p.x);
  float jag = uZap > 0.5 ? (envNoise(vec2(a * 6.0, uTime * 18.0)) - 0.5) * 0.12 : (envNoise(vec2(a * 4.0, 3.0)) - 0.5) * 0.05;
  float w = mix(0.16, 0.05, vAge);
  float d = abs(r - 0.82 - jag);
  float ring = smoothstep(w, 0.0, d);
  float inner = smoothstep(0.9, 0.0, r) * 0.25 * (1.0 - vAge);
  float fade = 1.0 - vAge;
  float alpha = (ring + inner) * fade;
  if (alpha < 0.01) discard;
  vec3 col = mix(uColor, uColor2, smoothstep(0.0, 0.08, d));
  gl_FragColor = vec4(col, alpha);
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

export interface SplashRings {
  readonly mesh: InstancedMesh;
  spawn(x: number, y: number, now: number): void;
  dispose(): void;
}

export function createSplashRings(kind: VoidKind, colors: { ring: ColorRepresentation; edge: ColorRepresentation }, env: EnvUniforms): SplashRings {
  const geo = new PlaneGeometry(MAX_RADIUS * 2, MAX_RADIUS * 2);
  geo.rotateX(-Math.PI / 2);
  const life = new Float32Array(SLOTS * 2).fill(-100);
  const lifeAttr = new InstancedBufferAttribute(life, 2);
  geo.setAttribute('aLife', lifeAttr);
  const zap = kind === 'grid';
  const mat = new ShaderMaterial({
    uniforms: {
      ...UniformsUtils.clone(UniformsLib.fog), uTime: env.uTime, uColor: { value: new Color(colors.ring) }, uColor2: { value: new Color(colors.edge) },
      uZap: { value: zap ? 1 : 0 },
    },
    vertexShader: VERTEX, fragmentShader: FRAGMENT, transparent: true, depthWrite: false, fog: true,
    blending: zap ? AdditiveBlending : NormalBlending,
  });
  const mesh = new InstancedMesh(geo, mat, SLOTS);
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  mesh.name = 'splash-rings';
  const m = new Matrix4();
  for (let i = 0; i < SLOTS; i++) mesh.setMatrixAt(i, m.makeTranslation(0, VOID_Y, 0));
  let next = 0;
  return {
    mesh,
    spawn(x, y, now) {
      for (let k = 0; k < 2; k++) {
        const i = next;
        next = (next + 1) % SLOTS;
        mesh.setMatrixAt(i, m.makeTranslation(x, VOID_Y + 0.02 + k * 0.01, y));
        life[i * 2] = now + k * 0.18;
        life[i * 2 + 1] = k === 0 ? 1 : 0.7;
      }
      mesh.instanceMatrix.needsUpdate = true;
      lifeAttr.needsUpdate = true;
    },
    dispose() {
      geo.dispose();
      mat.dispose();
    },
  };
}
